import { KeyboardEvent, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { providersService } from "services/providers/providers.service"
import { Provider, ProviderGeoRestriction } from "services/providers/type"

import { Modal } from "./Modal"

interface Props {
  open: boolean
  provider: Provider | null
  existing: ProviderGeoRestriction | null
  onClose: () => void
  onSaved: (restriction: ProviderGeoRestriction | null) => void
}

function CountryTag({ code, onRemove }: { code: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-black/[0.06] dark:bg-white/10 text-[rgba(6,7,21,1)] dark:text-white border border-black/10 dark:border-white/10">
      {code.toUpperCase()}
      <button type="button" onClick={onRemove} className="opacity-50 hover:opacity-100 leading-none">
        ✕
      </button>
    </span>
  )
}

function CountryInput({
  label,
  note,
  countries,
  onChange
}: {
  label: string
  note: string
  countries: string[]
  onChange: (next: string[]) => void
}) {
  const [input, setInput] = useState("")

  const add = () => {
    const val = input.trim().toUpperCase()
    if (!val) return
    if (val.length !== 2) {
      toast.error("Country code must be 2 letters")
      return
    }

    if (countries.includes(val)) {
      setInput("")
      return
    }
    onChange([...countries, val])
    setInput("")
  }

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      add()
    }
  }

  const isValid = (code: string) => /^[A-Z]{2}$/.test(code)

  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-sm font-medium text-[rgba(6,7,21,1)] dark:text-white">{label}</p>
        <p className="text-xs opacity-50">{note}</p>
      </div>

      <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
        {countries.length === 0 && <span className="text-xs opacity-40 italic">None</span>}
        {countries.map((c) => (
          <CountryTag key={c} code={c} onRemove={() => onChange(countries.filter((x) => x !== c))} />
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="UA, US, DE…"
          maxLength={2}
          minLength={2}
          value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase())}
          onKeyDown={onKey}
          className="input input-bordered input-sm flex-1 bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
        />
        <button
          type="button"
          onClick={add}
          disabled={!isValid(input)}
          className="btn btn-sm btn-ghost border border-black/10 dark:border-white/10"
        >
          + Add
        </button>
      </div>
    </div>
  )
}

export function ModalProviderGeo({ open, provider, existing, onClose, onSaved }: Props) {
  const [blocked, setBlocked] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setBlocked(existing?.blocked_countries ?? [])
    }
  }, [open, existing])

  const handleSave = async () => {
    if (!provider) return
    setSaving(true)

    if (blocked.length === 0 && existing) {
      const ok = await providersService.deleteRestriction(provider.identifier)
      setSaving(false)
      if (ok) {
        toast.success("Geo restrictions removed")
        onSaved(null)
      }
      return
    }

    if (blocked.length === 0) {
      setSaving(false)
      onClose()
      return
    }

    const ok = await providersService.upsertRestriction(provider.identifier, { blocked_countries: blocked })
    setSaving(false)
    if (ok) {
      toast.success("Geo restrictions saved")
      onSaved({
        id: existing?.id ?? "",
        provider_id: provider.identifier,
        blocked_countries: blocked,
        created_at: existing?.created_at ?? new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
    }
  }

  const handleDelete = async () => {
    if (!provider || !existing) return
    setSaving(true)
    const ok = await providersService.deleteRestriction(provider.identifier)
    setSaving(false)
    if (ok) {
      toast.success("Geo restrictions removed")
      onSaved(null)
    }
  }

  if (!provider) return null

  return (
    <Modal open={open} handler={onClose}>
      <div className="modal-box bg-white dark:bg-[#0C0C1A] text-[rgba(6,7,21,1)] dark:text-white w-[480px] max-w-full rounded-2xl border border-black/10 dark:border-white/10 shadow-xl">
        <h3 className="font-bold text-xl mb-2">Provider GEO Restrictions</h3>
        <div className="opacity-50 mb-4 leading-relaxed gap-2 flex flex-col text-sm">
          <p>
            <strong>Blocked countries</strong> — these countries cannot see the provider.
          </p>
          <div className="bg-warning/20 border-warning/80 p-2 rounded-sm text-white my-2">
            Format: Use ISO 3166-1 alpha-2 country codes (UA, US, DE…).
          </div>
        </div>

        <CountryInput
          label="Blocked countries (blacklist)"
          note="These countries cannot see this provider"
          countries={blocked}
          onChange={setBlocked}
        />

        <div className="modal-action mt-6 flex justify-between items-center">
          {existing && (
            <button
              type="button"
              disabled={saving}
              onClick={handleDelete}
              className="btn btn-sm btn-ghost text-error opacity-70 hover:opacity-100"
            >
              Remove restrictions
            </button>
          )}

          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="btn btn-sm btn-ghost border border-black/10 dark:border-white/10">
              Cancel
            </button>
            <button type="button" disabled={saving} onClick={handleSave} className="btn btn-sm btn-primary">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
